<template>
    <v-container fluid>
        <div class="header">
            <div class="left">
                <language-button
                    :class="{ hidden: !showLanguageButton }"
                    color="var(--desktop-view-text-color)"
                    class="language-button"
                />
                <div class="station-text-wrapper" />
                <span>
                    <train-icon
                        width="6vh"
                        height="auto"
                        color="var(--desktop-view-text-color)"
                    />
                </span>
                <span class="station-text">
                    <span>{{ $t("routesInfos.station") }}</span>
                    <span class="station-name">{{ stationName }}</span>
                </span>
                <span class="logo default">
                    <ose-logo-icon />
                </span>
            </div>
            <div class="d-flex justify-center full">
                <span class="logo">
                    <ose-logo-icon />
                </span>
            </div>
        </div>
        <div class="body">
            <div class="socials-container">
                <div class="mb-5">
                    <a target="_blank" href="https://www.facebook.com/ose1869">
                        <v-icon class="social-icon" color="var(--desktop-view-text-color)">
                            mdi-facebook
                        </v-icon>
                    </a>
                </div>
                <div class="mb-5">
                    <a
                        target="_blank"
                        href="https://www.linkedin.com/company/ose-hellenic-railways/"
                    >
                        <v-icon class="social-icon" color="var(--desktop-view-text-color)">
                            mdi-linkedin
                        </v-icon>
                    </a>
                </div>
                <div class="mb-5">
                    <a target="_blank" href="https://www.instagram.com/ose.gr/">
                        <v-icon class="social-icon" color="var(--desktop-view-text-color)">
                            mdi-instagram
                        </v-icon>
                    </a>
                </div>
                <span class="social-text ml-1">{{ $t("routesInfos.social") }}</span>
            </div>
            <div class="info-container">
                <div class="routes-container">
                    <div class="table-header">
                        <div class="header-text">{{ $t("routesInfos.routes") }}</div>
                        <div class="clock">
                            {{ time }}
                        </div>
                    </div>
                    <hr class="border-header mx-5">
                    <div class="mx-1 px-0">
                        <div
                            class="route-table"
                            :class="{ announcements: announcements.length > 0 }"
                        >
                            <v-data-table
                                :key="`table-${language}`"
                                hide-default-footer
                                dark
                                dense
                                :loading="routes.length === 0"
                                :loading-text="$t('routesInfos.loading')"
                                :no-data-text="$t('routesInfos.noData')"
                                :headers="headers"
                                :items="routes"
                            >
                                <template #item.company="{ item }">
                                    <div class="item-cell">{{ item[routeLanguageKey].RUP }}</div>
                                </template>
                                <template #item.train="{ item }">
                                    <div class="item-cell">
                                        {{ formatText(item[routeLanguageKey].TRENO) }}
                                    </div>
                                </template>
                                <template #item.destinations="{ item }">
                                    <div class="item-cell">
                                        {{ formatText(item[routeLanguageKey].FROMTO) }}
                                    </div>
                                </template>
                                <template #item.gate="{ item }">
                                    <div class="item-cell">
                                        {{ item[routeLanguageKey].DR_PLATFORM }}
                                    </div>
                                </template>
                                <template #item.arrival="{ item }">
                                    <div class="item-cell">
                                        {{ arrivalData(item[routeLanguageKey]) }}
                                    </div>
                                </template>
                                <template #item.departure="{ item }">
                                    <div class="item-cell">
                                        {{ departureData(item[routeLanguageKey]) }}
                                    </div>
                                </template>
                                <template #item.delay="{ item }">
                                    <div
                                        class="item-cell"
                                        :class="[
                                            item[routeLanguageKey].PREVIOUSDELAY9 > 0
                                                ? 'bordered-td'
                                                : 'dash'
                                        ]"
                                    >
                                        <div class="delay-text">
                                            {{
                                                item[routeLanguageKey].PREVIOUSDELAY9 > 0
                                                    ? item[routeLanguageKey].PREVIOUSDELAY9 + "΄"
                                                    : "-"
                                            }}
                                        </div>
                                    </div>
                                </template>
                            </v-data-table>
                            <div
                                v-if="announcements.length > 0"
                                class="announcements-container"
                            >
                                <div class="marquee-position">
                                    <span
                                        v-for="announcement in announcements"
                                        :key="announcement.id"
                                    >
                                        {{ announcement.text }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="checkWindowSize" class="side-container">
                <div class="weather-box">
                    <div class="weather-area-text">
                        {{ area[areaName] }}
                    </div>
                    <vue-weather
                        class="ml-5"
                        api-key="1f4b70c1b4db7d231e56494dd08352dd"
                        units="uk"
                        latitude="37.9909516"
                        longitude="23.668128"
                        :language="language"
                        :hide-header="true"
                        :update-interval="60 * 60 * 1000"
                    />
                </div>
                <div class="d-flex w-100 video-wrapper">
                    <video
                        :src="getVideoUrl()"
                        class="custom-video"
                        controls
                        autoplay
                        loop
                        muted
                    />
                </div>
            </div>
        </div>
    </v-container>
</template>

<script>
    import VueWeather from '@/components/vue-weather-widget'
    import LanguageButton from '@/components/LanguageButton'
    import TrainIcon from '@/components/images/TrainIcon.vue'
    import OseLogoIcon from '@/components/images/OseLogoIcon.vue'
    import stringUtils from '@/utils/string-utils'

    export default {
        components: { TrainIcon, OseLogoIcon, VueWeather, LanguageButton },
        props: {
            routes: {
                type: Array,
                default: () => []
            },
            announcements: {
                type: Array,
                default: () => []
            },
            area: {
                type: Object,
                default: () => {}
            },
            screen: {
                type: Object,
                default: () => {}
            },
            time: {
                type: String,
                default: ''
            },
            checkWindowSize: {
                type: Boolean
            }
        },
        data() {
            return {
                videoUrl:
                    'https://s3.eu-west-1.amazonaws.com/ose.mpass.ltd//uploads/5536540c-48c3-4f8a-bc19-61395d6c2c63-ose_music2_1.mp4'
            }
        },
        computed: {
            headers() {
                return [
                    {
                        text: this.$t('routesInfos.company'),
                        value: 'company',
                        sortable: false
                    },
                    {
                        text: this.$t('routesInfos.train'),
                        value: 'train',
                        sortable: false
                    },
                    {
                        text: this.$t('routesInfos.destinations'),
                        value: 'destinations',
                        sortable: false
                    },
                    { text: this.$t('routesInfos.gate'), value: 'gate', sortable: false },
                    {
                        text: this.$t('routesInfos.arrival'),
                        value: 'arrival',
                        sortable: false
                    },
                    {
                        text: this.$t('routesInfos.departure'),
                        value: 'departure',
                        sortable: false
                    },
                    { text: this.$t('routesInfos.delay'), value: 'delay', sortable: false }
                ]
            },
            stationName() {
                return stringUtils.removeAccents(this.area[this.areaName]).toUpperCase()
            },
            language() {
                return this.$store.getters.getLanguage
            },
            routeLanguageKey() {
                return this.language === 'en' ? 'routesEn' : 'routesEl'
            },
            areaName() {
                return this.language === 'el' ? 'nameEl' : 'nameEn'
            },
            showLanguageButton() {
                return this.$store.getters.getMode !== 'auto'
            }
        },
        methods: {
            getVideoUrl() {
                return screen.video
                    ? screen.video.baseUrl + '/' + screen.video.name
                    : this.videoUrl
            },
            arrivalData(route) {
                if (this.area.areaCode === 28 || this.area.areaCode === 2) {
                    return route.DR_TANA === null ? '-' : route.DR_TANA.replace('.', ':')
                }
                return route.DR_TANA === null
                    ? route.DR_TAFIX.replace('.', ':')
                    : route.DR_TANA.replace('.', ':')
            },
            departureData(route) {
                return route.DR_TANA === null ? route.DR_TAFIX.replace('.', ':') : '-'
            },
            formatText(text) {
                return stringUtils.removeAccents(text).toUpperCase()
            }
        }
    }
</script>

<style lang="css" scoped>
.container {
  --desktop-view-text-color: white;
  /*--desktop-view-text-color: #ee7d11;*/

  background: #f5f5f5 url("../assets/ose-bg.svg") no-repeat center center;

  /*display: grid;*/
  /*grid-template-rows: 1fr 6fr;*/
  /*grid-template-columns: 8vw auto;*/
  display: flex;
  flex-direction: column;
  background-size: cover;
  min-height: 100vh;
  min-width: 100vw;
  width: auto;
  gap: 1rem;
  padding: 0 1rem 0 0;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
}

.header .left {
  display: flex;
  gap: 1rem;
  flex: 1;
}

.body {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1rem 0;
}

.default {
  margin-left: auto;
  display: flex !important;
}

.full {
  display: none !important;
}

.language-button {
  align-self: center;
  justify-self: center;
}

.language-button.hidden {
  visibility: hidden;
}

.station-text-wrapper {
  display: flex;
  align-items: center;
  gap: 2vw;
}

.station-text {
  text-align: left;
  font-weight: 700;
  font-size: 4vh;
  line-height: 4vh;
  color: var(--desktop-view-text-color);
  display: flex;
  flex-direction: column;
}

.station-name {
  max-width: 200px;
}

.socials-container {
  padding: 0;
  margin: 0;
  position: relative;
  max-width: 80px;
  align-self: flex-end;
  border-right: 2px solid #000000;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 80%;
}

.social-text {
  font-style: normal;
  font-weight: 400;
  font-size: 21px;
  line-height: 21px;
  color: var(--desktop-view-text-color);
  transform: rotate(180deg);
  writing-mode: vertical-rl;
  padding-top: 5vh;
}

.info-container {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
  justify-content: space-between;
  flex: 4;
}

.routes-container {
  background: #000000;
  border-radius: 20px;
  box-shadow: 0 8px 13px 3px rgb(40 40 40 / 20%);
  padding: 1.5rem 1rem;
  max-height: 80vh;
}

::v-deep tbody {
  tr:hover {
    background-color: transparent !important;
  }
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 1rem;
}

.header-text {
  font-weight: 700;
  font-size: 45px;
  color: #7b7b7b;
}

.clock {
  display: flex;
  font-family: "Gas Font", sans-serif !important;
  text-align: center;
  font-size: 2.375rem;
  color: #ffffff;
  justify-content: end;
}

.border-header {
  border-bottom: 1px #7b7b7b !important;
}

::v-deep .v-data-table {
  /*  !*font-family: arial, sans-serif !important;*!*/
  /*  border-collapse: collapse !important;*/
  /*  width: 100% !important;*/
  padding-left: 1rem !important;
  padding-right: 1rem !important;
  background-color: transparent !important;
}

::v-deep th {
  /*border-bottom: none !important;*/
  /*text-align: left !important;*/
  /*font-weight: 700 !important;*/
  /*font-size: 20px !important;*/
  /*line-height: 24px !important;*/
  color: #ee7d11 !important;
  text-wrap: nowrap;
  /*padding: 32px 10px 0 !important;*/
}

::v-deep .v-data-table__wrapper {
  background-color: black !important;
  max-height: 66vh;
  overflow: auto;
}

.announcements ::v-deep .v-data-table__wrapper {
  max-height: 64vh;
}

::v-deep .text-start {
  text-align: left !important;
  padding: 5px !important;
  border: none !important;
  /*background: #282828 !important;*/
  /*border: 10px solid #000000 !important;*/
  font-weight: 500 !important;
  font-size: 3vh !important;
  /*line-height: 27px !important;*/
}

.item-cell {
  background-color: #282828 !important;
  padding: 5px 15px !important;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  min-height: 10vh !important;
}

.wider-td {
  min-width: 250px !important;
}

.mid-td {
  min-width: 200px !important;
}

.bordered-td {
  width: 100%;
  height: 100%;
  box-shadow: inset 0 0 0 3px #ee7d11 !important;
}

.side-container {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.5rem;
  flex: 1;
}

.weather-box {
  background: #dedede;
  border-radius: 20px;
  width: 100%;
  padding: 30px 1rem 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: center;
}

.delay-text {
  font-weight: 700;
  color: #ee7d11;
}

video {
  border-radius: 20px;
  box-shadow: 0 8px 13px 3px rgb(40 40 40 / 20%);
  pointer-events: none;
  width: 100%;
}

.logo {
  width: 15vh;
  height: auto;
  align-self: center;
}

.announcements-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
  padding-top: 0.5rem;
}

.marquee-position {
  display: flex;
  align-items: center;
  width: 100%;
  font-weight: 700;
  font-size: 2.5vh;
  line-height: 2.5vh;
  color: #ffffff;
  bottom: 20px;
  animation: scroll-left 20s linear infinite;
  transform: translateX(110%);
  height: 100%;
}

@keyframes scroll-left {
  0% {
    opacity: 0;
  }

  20% {
    opacity: 1;
  }

  80% {
    opacity: 1;
  }

  100% {
    transform: translateX(-110%);
    opacity: 0;
  }
}

@media (min-width: 1920px) {
  .default {
    display: none !important;
  }

  .full {
    display: flex !important;
  }

  .clock {
    font-size: 4.375rem;
  }

  .header-text {
    font-size: 65px;
  }

  .side-container {
    gap: 2rem;
    justify-content: start;
  }

  ::v-deep .text-start {
    font-size: 3.25vh !important;
  }
}

@media screen and (min-width: 2560px) {
  .container {
    gap: 2rem;
    grid-template-columns: 5vw auto 500px;
  }

  .social-text {
    font-size: 1.8vh;
  }

  .social-icon {
    font-size: 1.2vw !important;
  }

  .routes-container {
    padding: 2rem;
  }

  .header-text,
  .clock {
    font-size: 2.8vw !important;
  }

  ::v-deep .text-start {
    font-size: 2.5vh !important;
    padding: 10px !important;
  }

  ::v-deep .item-cell {
    min-height: 100px !important;
  }

  .weather-box {
    max-width: 90%;
    padding: 2rem;
  }

  .weather-area-text {
    font-size: 2.5vh;
    text-align: center;
  }

  .custom-video {
    max-width: 90%;
    margin: 0 auto;
    max-height: fit-content;
  }

  ::v-deep .text-start {
    font-size: 4vh !important;
  }
}

@media (min-width: 3840px) {
  .container {
    grid-template-columns: 5vw auto 800px;
  }

  .socials-container {
    max-width: 150px;
  }

  .social-icon {
    font-size: 1.5vw !important;
  }

  .social-text {
    font-size: 2vh;
  }
}

@media (aspect-ratio: 5/4) {
  .container {
    background-image: url("../assets/ose-bg-4-3.svg");
  }

  .station-text-wrapper {
    padding-left: 3.5vw;
  }
}
</style>
